
import * as signalR from '@microsoft/signalr';
import { hubConnection, reportHubLive, retryDelayMs } from './hubConnection';

// A refetch signal, never the read model itself.
export type ReadModelChange = {
  modelType: string;
  entityId: string;
  timestamp: string;
};

export type ReadModelChangeHandler = (change: ReadModelChange) => void;

export type ResyncHandler = () => void;

export interface ReadModelsHub {
  subscribe(modelType: string, handler: ReadModelChangeHandler): () => void;
  watchEntity(modelType: string, entityId: string, handler: ReadModelChangeHandler): () => void;
  // Fires when pushes may have been missed: on reconnect, and on a slow poll while the hub is down.
  onResync(handler: ResyncHandler): () => void;
}

const RESYNC_POLL_MS = 15_000;

// Self-healing: reconnect never gives up, a failed start retries with capped
// backoff, and joins are intent-tracked so every recovery re-joins and resyncs.
// Push is an enhancement: while the hub is down the resync poll keeps every
// watcher converging over HTTP — a page may wait minutes on a single push.
export class ReadModelsHubClient implements ReadModelsHub {
  private connection: signalR.HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  private handlers = new Map<string, Set<ReadModelChangeHandler>>();
  private watched = new Set<string>();
  private watchedEntities = new Set<string>();
  private joined = new Set<string>();
  private resyncHandlers = new Set<ResyncHandler>();
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private restartTimer: ReturnType<typeof setTimeout> | null = null;
  private restartAttempts = 0;

  constructor(private readonly path: string) {}

  subscribe(modelType: string, handler: ReadModelChangeHandler): () => void {
    let set = this.handlers.get(modelType);
    if (!set) {
      set = new Set();
      this.handlers.set(modelType, set);
    }
    set.add(handler);
    this.watched.add(modelType);

    void this.connectAndJoin();

    return () => this.unsubscribe(modelType, handler);
  }

  watchEntity(modelType: string, entityId: string, handler: ReadModelChangeHandler): () => void {
    let set = this.handlers.get(modelType);
    if (!set) {
      set = new Set();
      this.handlers.set(modelType, set);
    }
    set.add(handler);
    this.watchedEntities.add(`${modelType} ${entityId}`);

    void this.connectAndJoin();

    return () => this.unsubscribeEntity(modelType, entityId, handler);
  }

  onResync(handler: ResyncHandler): () => void {
    this.resyncHandlers.add(handler);
    return () => {
      this.resyncHandlers.delete(handler);
    };
  }

  private async connectAndJoin(): Promise<void> {
    try {
      await this.ensureStarted();
    } catch (err) {
      console.warn(`[hub ${this.path}] start failed`, err);
      this.setLive(false);
      this.scheduleRestart();
      return;
    }
    const conn = this.connection;
    if (!conn) return;
    for (const modelType of [...this.watched]) {
      await this.join(conn, `owner:${modelType}`, () => conn.invoke('WatchOwner', modelType));
    }
    for (const key of [...this.watchedEntities]) {
      const sep = key.indexOf(' ');
      if (sep <= 0) continue;
      const modelType = key.slice(0, sep);
      const entityId = key.slice(sep + 1);
      await this.join(conn, `entity:${key}`, () => conn.invoke('WatchEntity', modelType, entityId));
    }
  }

  private async join(
    conn: signalR.HubConnection,
    joinKey: string,
    invoke: () => Promise<unknown>,
  ): Promise<void> {
    if (this.joined.has(joinKey) || this.connection !== conn) return;
    // Claimed before the invoke so concurrent subscribes join once.
    this.joined.add(joinKey);
    try {
      await invoke();
    } catch (err) {
      this.joined.delete(joinKey);
      console.warn(`[hub ${this.path}] ${joinKey} failed`, err);
      // A join refused on a live connection would be refused again.
      if (conn.state !== signalR.HubConnectionState.Connected) this.scheduleRestart();
    }
  }

  private ensureStarted(): Promise<void> {
    if (this.startPromise) return this.startPromise;

    const conn = hubConnection(this.path, true);

    conn.on('EntityChanged', (change: ReadModelChange) => this.dispatch(change));
    // The node's Redis backplane came back: pushes published meanwhile were lost.
    conn.on('Resync', () => this.resync());

    conn.onreconnecting(() => this.setLive(false));

    conn.onreconnected(() => {
      this.joined.clear();
      this.setLive(true);
      void this.connectAndJoin().then(() => this.resync());
    });

    conn.onclose(() => {
      if (this.connection !== conn) return;
      this.connection = null;
      this.startPromise = null;
      this.joined.clear();
      this.setLive(false);
      this.scheduleRestart();
    });

    this.connection = conn;
    this.startPromise = conn
      .start()
      .then(() => {
        this.restartAttempts = 0;
        this.setLive(true);
      })
      .catch((err) => {
        if (this.connection === conn) {
          this.startPromise = null;
          this.connection = null;
        }
        throw err;
      });
    return this.startPromise;
  }

  private scheduleRestart(): void {
    if (this.restartTimer || this.handlers.size === 0) return;
    const delay = retryDelayMs(this.restartAttempts);
    this.restartAttempts += 1;
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      if (this.handlers.size === 0) return;
      const stale = this.connection;
      if (stale && stale.state === signalR.HubConnectionState.Disconnected) {
        this.connection = null;
        this.startPromise = null;
        this.joined.clear();
        void stale.stop().catch(() => {});
      }
      void this.connectAndJoin().then(() => {
        if (this.connection?.state === signalR.HubConnectionState.Connected) {
          this.resync();
        }
      });
    }, delay);
  }

  private unsubscribeEntity(modelType: string, entityId: string, handler: ReadModelChangeHandler): void {
    const set = this.handlers.get(modelType);
    if (!set) return;
    set.delete(handler);
    if (set.size > 0) return;

    this.handlers.delete(modelType);
    this.watchedEntities.delete(`${modelType} ${entityId}`);
    this.joined.delete(`entity:${modelType} ${entityId}`);
    const conn = this.connection;
    if (conn) {
      void (async () => {
        try {
          await conn.invoke('UnwatchEntity', modelType, entityId);
        } catch {
          /* ignore — connection may already be closing */
        }
      })();
    }

    if (this.handlers.size === 0) void this.teardown();
  }

  private dispatch(change: ReadModelChange): void {
    const set = this.handlers.get(change.modelType);
    if (!set) return;
    for (const handler of [...set]) handler(change);
  }

  private resync(): void {
    for (const handler of [...this.resyncHandlers]) handler();
  }

  private setLive(live: boolean): void {
    const down = !live && this.handlers.size > 0;
    reportHubLive(this.path, !down);
    if (!down) {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
      return;
    }
    this.pollTimer ??= setInterval(() => {
      if (!globalThis.document?.hidden) this.resync();
    }, RESYNC_POLL_MS);
  }

  private unsubscribe(modelType: string, handler: ReadModelChangeHandler): void {
    const set = this.handlers.get(modelType);
    if (!set) return;
    set.delete(handler);
    if (set.size > 0) return;

    this.handlers.delete(modelType);
    this.watched.delete(modelType);
    this.joined.delete(`owner:${modelType}`);
    const conn = this.connection;
    if (conn) {
      void (async () => {
        try {
          await conn.invoke('UnwatchOwner', modelType);
        } catch {
          /* ignore — connection may already be closing */
        }
      })();
    }

    if (this.handlers.size === 0) void this.teardown();
  }

  private async teardown(): Promise<void> {
    const conn = this.connection;
    this.connection = null;
    this.startPromise = null;
    this.watched.clear();
    this.watchedEntities.clear();
    this.joined.clear();
    this.restartAttempts = 0;
    this.setLive(true);
    if (this.restartTimer) {
      clearTimeout(this.restartTimer);
      this.restartTimer = null;
    }
    if (conn) {
      try {
        await conn.stop();
      } catch {
        /* ignore */
      }
    }
  }
}

export const readModelsHub: ReadModelsHub = new ReadModelsHubClient('/hubs/readmodels');

export const adminReadModelsHub: ReadModelsHub = new ReadModelsHubClient('/api/admin/hubs/readmodels');
